import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { FaUsers, FaFileAlt, FaExclamationTriangle, FaHeartbeat } from "react-icons/fa";
import Layout from '../components/Layout';
import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../utils/axios';

export default function AdminDashboard() {
  const [stats, setStats] = useState({
    totalUsers: 0,
    documentsUploaded: 0,
    flaggedVitals: 0,
    vitalsToday: 0
  });
  const [vitalsChartData, setVitalsChartData] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [recentUsers, setRecentUsers] = useState([]);
  const [recentDocuments, setRecentDocuments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await api.get('/admin/dashboard/stats');
        setStats(response.data);
      } catch (error) {
        console.error('Error fetching stats:', error);
        if (error.response?.status !== 200) {
          toast.error('Failed to fetch dashboard statistics');
        }
      }
    };

    const fetchVitalsChartData = async () => {
      try {
        const response = await api.get('/admin/dashboard/vitals-chart');
        setVitalsChartData(response.data);
      } catch (error) {
        console.error('Error fetching vitals chart data:', error);
        if (error.response?.status !== 200) {
          toast.error('Failed to fetch vitals chart data');
        }
      }
    };

    const fetchRecentActivity = async () => {
      try {
        const response = await api.get('/admin/dashboard/recent-activity');
        setRecentActivity(response.data);
      } catch (error) {
        console.error('Error fetching recent activity:', error);
        if (error.response?.status !== 200) {
          toast.error('Failed to fetch recent activity');
        }
      }
    };

    const fetchRecentUsers = async () => {
      try {
        const response = await api.get('/admin/users');
        setRecentUsers(response.data.users.slice(0, 5));
      } catch (error) {
        console.error('Error fetching recent users:', error);
        if (error.response?.status !== 200) {
          toast.error('Failed to fetch recent users');
        }
      }
    };

    const fetchRecentDocuments = async () => {
      try {
        const response = await api.get('/admin/users/logs');
        setRecentDocuments(response.data.data.documents.slice(0, 5));
      } catch (error) {
        console.error('Error fetching recent documents:', error);
        if (error.response?.status !== 200) {
          toast.error('Failed to fetch recent documents');
        }
      }
    };

    const fetchAllData = async () => {
      setLoading(true);
      try {
        await Promise.all([
          fetchStats(),
          fetchVitalsChartData(),
          fetchRecentActivity(),
          fetchRecentUsers(),
          // fetchRecentDocuments()
        ]);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAllData();
  }, []);

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 space-y-6">
        <h1 className="text-2xl font-bold">Admin Dashboard</h1>

        {/* Metrics */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white shadow rounded-lg p-4">
            <div className="flex items-center gap-2 text-gray-600 mb-1">
              <FaUsers className="w-4 h-4" />
              <h2 className="text-sm">Users</h2>
            </div>
            <p className="text-2xl font-semibold">{stats.totalUsers}</p>
          </div>
          <div className="bg-white shadow rounded-lg p-4">
            <div className="flex items-center gap-2 text-gray-600 mb-1">
              <FaFileAlt className="w-4 h-4" />
              <h2 className="text-sm">Documents</h2>
            </div>
            <p className="text-2xl font-semibold">{stats.documentsUploaded}</p>
          </div>
          <div className="bg-white shadow rounded-lg p-4">
            <div className="flex items-center gap-2 text-gray-600 mb-1">
              <FaExclamationTriangle className="w-4 h-4 text-yellow-500" />
              <h2 className="text-sm">Flagged Vitals Today</h2>
            </div>
            <p className="text-2xl font-semibold">{stats.flaggedVitals}</p>
          </div>
          <div className="bg-white shadow rounded-lg p-4">
            <div className="flex items-center gap-2 text-gray-600 mb-1">
              <FaHeartbeat className="w-4 h-4 text-red-500" />
              <h2 className="text-sm">Vitals Today</h2>
            </div>
            <p className="text-2xl font-semibold">{stats.vitalsToday}</p>
          </div>
        </div>

        {/* Chart */}
        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-lg mb-4">Vitals Submitted This Week</h2>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={vitalsChartData}>
              <XAxis dataKey="day" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="totalVitals" fill="#8884d8" name="Total Vitals" />
              <Bar dataKey="vitalsByType.blood_pressure" fill="#82ca9d" name="Blood Pressure" />
              <Bar dataKey="vitalsByType.blood_sugar" fill="#ffc658" name="Blood Sugar" />
              <Bar dataKey="vitalsByType.heart_rate" fill="#ff8042" name="Heart Rate" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Activity + Actions */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white shadow rounded-lg p-4 space-y-2">
            <h2 className="text-lg font-semibold">Recent Activity</h2>
            {recentActivity.length > 0 ? (
              <ul className="space-y-3">
                {recentActivity.map((activity, idx) => (
                  <li key={idx} className="flex items-start gap-3 p-2 hover:bg-gray-50 rounded">
                    <div className={`w-2 h-2 rounded-full mt-2 ${
                      activity.type === 'vital' ? 'bg-blue-500' :
                      activity.type === 'document' ? 'bg-green-500' :
                      'bg-purple-500'
                    }`} />
                    <div className="flex-1">
                      <p className="text-sm text-gray-600">{activity.message}</p>
                      <p className="text-xs text-gray-400 mt-1">
                        {new Date(activity.timestamp).toLocaleString()}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-gray-500 text-sm">No recent activity</p>
            )}
          </div>

          <div className="bg-white shadow rounded-lg p-4 space-y-4">
            <h2 className="text-lg font-semibold">Quick Actions</h2>
            <div className="flex flex-col space-y-2">
              <button className="bg-indigo-600 hover:bg-indigo-700 text-white py-2 px-4 rounded">View All Users</button>
              <button className="bg-rose-600 hover:bg-rose-700 text-white py-2 px-4 rounded">Review Flagged Vitals</button>
              <button className="bg-amber-500 hover:bg-amber-600 text-white py-2 px-4 rounded">Push Notification</button>
              <button className="bg-emerald-600 hover:bg-emerald-700 text-white py-2 px-4 rounded">Add New Admin</button>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
